import { Inbox, Plus, SearchX } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaProgress } from "@/shared/components/feedback/ColocaProgress";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { LIST_PAGE_SIZE } from "../../constants/payoutBatches.constants";
import type { PayoutBatch } from "../../types/payoutBatches.types";
import { shortBatchId } from "../../utils/batchLinks";
import { executionProgress, isExecutionStatus, isReviewStatus } from "../../utils/batchStatus";
import { selectedShare } from "../../utils/batchSummary";
import { BatchStatusPill } from "../detail/BatchStatusPill";
import { CorridorLabel } from "../detail/CorridorLabel";
import { BatchExpiryCountdown } from "./BatchExpiryCountdown";

export interface BatchesTableProps {
  rows: PayoutBatch[];
  isLoading: boolean;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowClick: (batch: PayoutBatch) => void;
  /** INTERNAL only: organization names to render instead of ids. */
  organizationNames: ReadonlyMap<string, string> | null;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** Omitted when the principal cannot upload (`payout_batches:write`): the empty state offers no button. */
  onNewBatch?: () => void;
}

/** One line per batch, with the strip in miniature: how much of the file is chosen, or how the run went. */
export function BatchesTable({
  rows,
  isLoading,
  page,
  total,
  onPageChange,
  onRowClick,
  organizationNames,
  hasActiveFilters,
  onClearFilters,
  onNewBatch,
}: BatchesTableProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");

  const columns = useMemo<ColocaDataTableColumn<PayoutBatch>[]>(() => {
    const organization: ColocaDataTableColumn<PayoutBatch>[] = organizationNames
      ? [
          {
            id: "organization",
            header: t("list_col_organization"),
            cellClassName: "max-w-48",
            cell: (row) => (
              <span className="block truncate" title={row.organization_id}>
                {organizationNames.get(row.organization_id) ?? (
                  <span className="font-mono text-xs text-muted-foreground">
                    {shortBatchId(row.organization_id)}
                  </span>
                )}
              </span>
            ),
          },
        ]
      : [];

    return [
      {
        id: "created",
        header: t("list_col_created"),
        cell: (row) => (
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            {formatDateTime(row.created_at, { locale: activeLanguage, includeTime: true })}
          </span>
        ),
      },
      ...organization,
      {
        id: "corridor",
        header: t("list_col_corridor"),
        cell: (row) => <CorridorLabel country={row.country} rail={row.rail} currency={row.currency} />,
      },
      {
        id: "rows",
        header: t("list_col_rows"),
        headerClassName: "w-44",
        cell: (row) => {
          const execution = isExecutionStatus(row.status);
          const progress = execution ? executionProgress(row.counters) : null;
          return (
            <div className="space-y-1">
              <span className="font-mono text-xs text-foreground tabular-nums">
                {execution
                  ? `${row.counters.paid} / ${progress?.max ?? row.counters.selected}`
                  : t("list_rows_progress", { selected: row.counters.selected, total: row.counters.total })}
              </span>
              {progress ? (
                <ColocaProgress
                  value={progress.done}
                  max={progress.max}
                  size="sm"
                  segments={progress.segments}
                  aria-label={t("list_col_rows")}
                />
              ) : (
                <ColocaProgress
                  value={selectedShare(row.counters)}
                  size="sm"
                  tone={isReviewStatus(row.status) ? "primary" : "neutral"}
                  aria-label={t("list_col_rows")}
                />
              )}
            </div>
          );
        },
      },
      {
        id: "debit",
        header: t("list_col_debit"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => (
          <ColocaAmountDisplay
            value={
              isExecutionStatus(row.status) ? row.totals.debit_actual : row.totals.debit_indicative_selected
            }
            currency={row.source_currency}
            fractionDigits={minorUnitsFor(row.source_currency)}
            size="sm"
          />
        ),
      },
      {
        id: "status",
        header: t("list_col_status"),
        cell: (row) => (
          <div className="flex flex-col items-start gap-1">
            <BatchStatusPill status={row.status} size="sm" />
            {isReviewStatus(row.status) && <BatchExpiryCountdown expiresAt={row.expires_at} />}
          </div>
        ),
      },
      {
        id: "file",
        header: t("list_col_file"),
        cellClassName: "max-w-52",
        cell: (row) => (
          <span className="block truncate text-xs text-muted-foreground" title={row.file_name}>
            {row.file_name}
          </span>
        ),
      },
    ];
  }, [activeLanguage, organizationNames, t]);

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      onRowClick={onRowClick}
      loading={isLoading}
      skeletonRows={6}
      minWidth={organizationNames ? 1120 : 960}
      empty={
        hasActiveFilters ? (
          <ColocaEmpty
            icon={SearchX}
            title={t("list_empty_filtered_title")}
            description={t("list_empty_filtered_description")}
            surface="card"
          >
            <ColocaButton variant="default" size="sm" onClick={onClearFilters}>
              {t("kpi_all")}
            </ColocaButton>
          </ColocaEmpty>
        ) : (
          <ColocaEmpty
            icon={Inbox}
            title={t("list_empty_title")}
            description={t("list_empty_description")}
            surface="card"
          >
            {onNewBatch && (
              <ColocaButton variant="primary" size="sm" icon={Plus} onClick={onNewBatch}>
                {t("action_new_batch")}
              </ColocaButton>
            )}
          </ColocaEmpty>
        )
      }
      footer={
        total > LIST_PAGE_SIZE ? (
          <ColocaPagination
            page={page}
            perPage={LIST_PAGE_SIZE}
            total={total}
            withSeparator={false}
            size="sm"
            onPageChange={onPageChange}
          />
        ) : undefined
      }
    />
  );
}
